import { timezoneStore } from "@/api/hooks/useConfig";
import { DatePicker } from "@/components/v2/DatePicker";
import type { PresetKey } from "@/components/v2/DatePicker/constants";
import {
  getComparisonTimeFilter,
  getTimeFilter,
} from "@/components/v2/DatePicker/helpers";
import { PdfPreviewButton } from "@/components/v2/PdfPreview";
import { TenantCategoriesSelector } from "@/components/v2/TenantCategoriesSelector";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";

import { useCallback, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

import { ContaminationByTenant } from "./components/contaminationByTenant";
import { ContaminationHeader } from "./components/contaminationHeader";
import { ContaminationTrend } from "./components/contaminationTrend";
import { KpiNumbersContaminationPage } from "./components/kpiNumbers";
import { LostRecyclingRate } from "./components/lostRecyclingRate";
import { ContaminationPageContext } from "./context";

export const Contamination = () => {
  const { timezone } = timezoneStore();
  const [dateRange, setDateRange] = useState<DateRange>();
  const [datePreset, setDatePreset] = useState<PresetKey>();
  const [selectedTenantCategoryIds, setSelectedTenantCategoryIds] = useState<
    string[] | null
  >(null);

  const onDateAndPresetChange = useCallback(
    (range: DateRange, preset: PresetKey) => {
      setDateRange(range);
      setDatePreset(preset);
    },
    [],
  );

  const timeFilter = useMemo(() => getTimeFilter(dateRange), [dateRange]);

  const contextValue = useMemo(
    () => ({
      dateRange,
      datePreset,
      timeFilter,
      comparisonTimeFilter: getComparisonTimeFilter({
        datePreset,
        dateRange,
        timezone,
      }),
      selectedTenantCategoryIds,
    }),
    [dateRange, datePreset, timezone, timeFilter, selectedTenantCategoryIds],
  );

  const dataView = useMemo(
    () => (
      <ViewWithLoader isLoading={!selectedTenantCategoryIds?.length}>
        <ContaminationPageContext.Provider value={contextValue}>
          <>
            <div className="mt-6">
              <KpiNumbersContaminationPage />
            </div>

            <div className="mt-6">
              <ContaminationTrend />
            </div>

            <div className="mt-6">
              <ContaminationByTenant />
            </div>

            <div className="mt-6">
              <LostRecyclingRate />
            </div>
          </>
        </ContaminationPageContext.Provider>
      </ViewWithLoader>
    ),
    [contextValue, selectedTenantCategoryIds],
  );

  const pdfView = useMemo(
    () => (
      <div className="h-full min-h-[660px] w-full px-[33px] py-[44px]">
        <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between">
          <ContaminationHeader />
          <div className="flex items-center gap-3">
            <TenantCategoriesSelector
              timeFilter={timeFilter}
              value={selectedTenantCategoryIds}
              onChange={setSelectedTenantCategoryIds}
            />
            <DatePicker
              value={dateRange}
              presetValue={datePreset}
              onChange={onDateAndPresetChange}
            />
          </div>
        </div>
        {dataView}
      </div>
    ),
    [
      onDateAndPresetChange,
      dataView,
      dateRange,
      selectedTenantCategoryIds,
      setSelectedTenantCategoryIds,
      timeFilter,
      datePreset,
    ],
  );

  const view = useMemo(
    () => (
      <div className="h-full min-h-[660px] w-full px-[33px] py-[44px]">
        <div className="flex items-center justify-between">
          <ContaminationHeader />
          <div className="flex items-center gap-3">
            <TenantCategoriesSelector
              timeFilter={timeFilter}
              value={selectedTenantCategoryIds}
              onChange={setSelectedTenantCategoryIds}
            />
            <DatePicker
              value={dateRange}
              presetValue={datePreset}
              onChange={onDateAndPresetChange}
            />
            <PdfPreviewButton
              exportFileNameKey="contaminationPage:exportFileName"
              buttonTextKey="contaminationPage:previewButtonText"
              pdfView={pdfView}
            />
          </div>
        </div>
        {dataView}
      </div>
    ),
    [
      onDateAndPresetChange,
      dataView,
      dateRange,
      pdfView,
      selectedTenantCategoryIds,
      setSelectedTenantCategoryIds,
      timeFilter,
      datePreset,
    ],
  );

  return view;
};
